<div class="p-4 mx-auto grid gap-5 text-slate-900">

  <!-- ── Auswahl ──────────────────────────────────────────────────────────── -->
  <section class="rounded-2xl border border-slate-900/10 bg-white p-4 shadow-sm">
    <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
      <h2 class="m-0 text-[15px] font-bold">Schedule Translator Debugger</h2>
      <span class="inline-flex items-center gap-1.5 rounded-full bg-slate-900/5 px-2.5 py-[3px] text-[11px] text-slate-500">
        GET <code>{{ path }}?date={{ date() }}</code>
      </span>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <mat-form-field appearance="outline" class="min-w-[260px]" subscriptSizing="dynamic">
        <mat-label>Standort</mat-label>
        <mat-select [value]="selectedLocationId()" (valueChange)="setLocationId($event)">
          @for (loc of locationState.locations(); track loc.id) {
            <mat-option [value]="loc.id">{{ loc.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <div class="flex items-center gap-1">
        <button mat-icon-button type="button" (click)="shiftDay(-1)" aria-label="Vorheriger Tag">
          <mat-icon>chevron_left</mat-icon>
        </button>
        <input
          type="date"
          class="rounded-[10px] border border-slate-900/20 px-2.5 py-2 text-sm"
          [ngModel]="date()"
          (ngModelChange)="$event && date.set($event)"
        />
        <button mat-icon-button type="button" (click)="shiftDay(1)" aria-label="Nächster Tag">
          <mat-icon>chevron_right</mat-icon>
        </button>
        <button mat-stroked-button type="button" (click)="today()">Heute</button>
        <button mat-stroked-button type="button" (click)="reload()" [disabled]="loading()">
          <mat-icon>refresh</mat-icon>
          Neu laden
        </button>
      </div>

      @if (loading()) {
        <span class="text-xs text-slate-500">Lädt…</span>
      }
    </div>
  </section>

  @if (errorMessage()) {
    <section class="rounded-2xl border border-red-600/40 bg-red-600/[0.07] p-4 text-[13px] text-red-900" role="alert">
      {{ errorMessage() }}
    </section>
  }

  @if (response() && solutions().length === 0) {
    <section class="rounded-2xl border border-slate-900/10 bg-white p-4 text-[13px] text-slate-500">
      Für diesen Standort gibt es am {{ date() }} keine Optimierer-Lösung.
    </section>
  }

  <mat-accordion multi class="debug-accordion">
  <!-- ── Je Lösung: Zeitschritte | Fahrpläne ──────────────────────────────── -->
  @for (solution of solutions(); track solution.id) {
    @let times = actionTimes(solution);
    <mat-expansion-panel [expanded]="true">
      <mat-expansion-panel-header>
        <mat-panel-title>Lösung #{{ solution.id }} · Version {{ solution.version ?? '–' }}</mat-panel-title>
        <mat-panel-description>{{ solution.status }} · {{ solution.schedules?.length ?? 0 }} Fahrpläne</mat-panel-description>
      </mat-expansion-panel-header>
      <div class="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13px]">
        <span class="text-[15px] font-bold">Lösung #{{ solution.id }}</span>
        <span>Version {{ solution.version ?? '–' }}</span>
        <span
          class="rounded-full px-2 py-[1px] text-[11px] font-semibold"
          [class]="solution.status === 'ACTIVE' ? 'bg-emerald-600/15 text-emerald-800' : 'bg-slate-900/5 text-slate-500'"
        >
          {{ solution.status }}
        </span>
        <span class="text-slate-500">erstellt {{ dateTime(solution.createdAt) }}</span>
        <span class="text-slate-500">Kosten {{ solution.costs | number: '1.2-4' }}</span>
        <span class="text-slate-500">Peak Bezug (peakConsumption) {{ solution.peakConsumption | number: '1.0-1' }}</span>
        <span class="text-slate-500">Peak Einspeisung (peakFeedin) {{ solution.peakFeedin | number: '1.0-1' }}</span>
      </div>

      <div class="grid items-start gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">

        <!-- Zeitschritte -->
        <div class="min-w-0">
          <div class="mb-1.5 flex items-center gap-2 text-[13px] font-semibold">
            <span>
              Zeitschritte <span class="font-normal text-slate-500">({{ solution.timesteps?.length ?? 0 }}, Europe/Vienna)</span>
            </span>
            <ng-container *ngTemplateOutlet="copyButton; context: { table: timestepTable, key: 'timesteps-' + solution.id }" />
          </div>
          <div class="scroll-panel">
            <table class="debug-table" #timestepTable>
              <thead>
                <tr>
                  @for (column of timestepColumns; track $index) {
                    <th [title]="column.field">{{ column.label }}<span class="field-name">({{ column.field }})</span></th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (step of solution.timesteps; track step.timestamp) {
                  @let stepTime = time(step.timestamp);
                  <tr [class.has-action]="times.has(stepTime)">
                    <td class="font-mono">{{ timestamp(step.timestamp) }}</td>
                    <td>{{ step.consumption | number: '1.0-1' }}</td>
                    <td>{{ step.feedin | number: '1.0-1' }}</td>
                    <td>{{ step.curtail | number: '1.0-1' }}</td>
                    <td>{{ step.charge | number: '1.0-1' }}</td>
                    <td>{{ step.discharge | number: '1.0-1' }}</td>
                    <td>{{ step.soc | number: '1.0-1' }}</td>
                    <td>{{ step.yieldWh | number: '1.0-1' }}</td>
                    <td>{{ step.loadWh | number: '1.0-1' }}</td>
                    <td>{{ ctPerKwh(step.priceConsumptionEurWh) | number: '1.2-2' }}</td>
                    <td>{{ ctPerKwh(step.priceFeedinEurWh) | number: '1.2-2' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="mt-1 text-[11px] text-slate-500">Hervorgehoben: zu dieser Uhrzeit feuert eine Fahrplan-Aktion.</div>
        </div>

        <!-- Fahrpläne -->
        <div class="min-w-0">
          <div class="mb-1.5 text-[13px] font-semibold">
            Fahrpläne
            <span class="font-normal text-slate-500">({{ solution.schedules?.length ?? 0 }}, {{ actionCount(solution) }} Aktionen)</span>
          </div>
          @if (!solution.schedules?.length) {
            <div class="rounded-[10px] bg-slate-900/[0.03] p-3 text-xs text-slate-500">
              Aus dieser Lösung wurde kein Fahrplan erstellt.
            </div>
          }
          <div class="scroll-panel grid content-start gap-3">
            @for (schedule of solution.schedules; track schedule.id) {
              <div
                class="rounded-[10px] border p-2.5"
                [class]="schedule.generated ? 'border-2 border-red-600 bg-red-600/[0.04]' : 'border-slate-900/10'"
              >
                <div class="mb-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs">
                  <span class="font-bold">Fahrplan #{{ schedule.id }}</span>
                  @if (schedule.generated) {
                    <span class="rounded-full bg-red-600 px-2 py-[1px] text-[11px] font-semibold text-white" title="generated: true">
                      automatisch erzeugt <span class="font-mono font-normal">(generated)</span>
                    </span>
                  }
                  <span class="text-slate-500">{{ schedule.type }}</span>
                  <span class="text-slate-500">erstellt {{ dateTime(schedule.created) }}</span>
                  <span class="text-slate-500">gültig bis {{ dateTime(schedule.expiresAt) }}</span>
                  <ng-container *ngTemplateOutlet="copyButton; context: { table: actionTable, key: 'schedule-' + schedule.id }" />
                </div>
                <table class="debug-table" #actionTable>
                  <thead>
                    <tr>
                      @for (column of actionColumns; track $index) {
                        <th [title]="column.field">{{ column.label }}<span class="field-name">({{ column.field }})</span></th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (action of schedule.actions; track action.id ?? $index) {
                      <tr>
                        <td class="font-mono">{{ actionTimestamp(action, solution) ?? '–' }}</td>
                        <td class="text-left">{{ action.type || '–' }}</td>
                        <td>{{ action.value ?? '–' }}</td>
                        <td>{{ action.minSoC ?? '–' }}</td>
                        <td>{{ action.maxPurchaseSoC ?? '–' }}</td>
                        <td class="text-left">
                          {{ action.registerName || '–' }}{{ action.registerIndex != null ? ' [' + action.registerIndex + ']' : '' }}
                        </td>
                        <td class="font-mono text-left text-slate-400">{{ action.cron || '–' }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        </div>
      </div>
    </mat-expansion-panel>
  }

  <!-- ── Vergleich: letzter manueller vs. letzter automatischer Fahrplan ──── -->
  @if (scheduleDiff(); as diff) {
    <mat-expansion-panel [expanded]="true">
      <mat-expansion-panel-header>
        <mat-panel-title>Vergleich manuell ↔ automatisch</mat-panel-title>
        <mat-panel-description>
          {{ diffCount(diff, 'changed') + diffCount(diff, 'manualOnly') + diffCount(diff, 'autoOnly') }} Abweichungen
          von {{ diff.rows.length }} Zeilen
        </mat-panel-description>
      </mat-expansion-panel-header>
      <div class="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13px]">
        <span class="text-slate-500">ohne Register-Felder (registerName, registerIndex)</span>
        <ng-container *ngTemplateOutlet="copyButton; context: { table: diffTable, key: 'diff' }" />
      </div>

      <div class="mb-3 grid gap-1 text-xs sm:grid-cols-2">
        <div>
          <span class="font-semibold">Manuell:</span>
          @if (diff.manual; as m) {
            Fahrplan #{{ m.schedule.id }} · Lösung #{{ m.solution.id }} · erstellt {{ dateTime(m.schedule.created) }}
            · {{ m.schedule.actions?.length ?? 0 }} Aktionen
          } @else {
            <span class="text-slate-500">kein manuell erstellter Fahrplan an diesem Tag</span>
          }
        </div>
        <div>
          <span class="font-semibold text-red-700">Automatisch:</span>
          @if (diff.auto; as a) {
            Fahrplan #{{ a.schedule.id }} · Lösung #{{ a.solution.id }} · erstellt {{ dateTime(a.schedule.created) }}
            · {{ a.schedule.actions?.length ?? 0 }} Aktionen
          } @else {
            <span class="text-slate-500">kein automatisch erzeugter Fahrplan an diesem Tag</span>
          }
        </div>
      </div>

      <div class="mb-2 flex flex-wrap gap-2 text-[11px]">
        <span class="diff-badge diff-same">gleich {{ diffCount(diff, 'same') }}</span>
        <span class="diff-badge diff-changed">abweichend {{ diffCount(diff, 'changed') }}</span>
        <span class="diff-badge diff-manualOnly">nur manuell {{ diffCount(diff, 'manualOnly') }}</span>
        <span class="diff-badge diff-autoOnly">nur automatisch {{ diffCount(diff, 'autoOnly') }}</span>
      </div>

      <div class="scroll-panel">
        <table class="debug-table diff-table" #diffTable>
          <thead>
            <tr>
              <th title="cron">Zeit<span class="field-name">(cron)</span></th>
              @for (column of diffFields; track column.field) {
                <th [title]="column.field" class="group-start-manual">Manuell {{ column.label }}<span class="field-name">({{ column.field }})</span></th>
              }
              @for (column of diffFields; track column.field) {
                <th [title]="column.field" class="group-start-auto">Auto {{ column.label }}<span class="field-name">({{ column.field }})</span></th>
              }
              <th class="text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            @for (row of diff.rows; track $index) {
              <tr [class]="'diff-row-' + row.status">
                <td class="font-mono">{{ row.time }}</td>
                @for (column of diffFields; track column.field) {
                  <td [class.diff-cell]="row.changed.includes(column.field)" [class.text-left]="column.field === 'type'">
                    {{ row.manual ? (row.manual[column.field] ?? '–') : '' }}
                  </td>
                }
                @for (column of diffFields; track column.field) {
                  <td [class.diff-cell]="row.changed.includes(column.field)" [class.text-left]="column.field === 'type'">
                    {{ row.auto ? (row.auto[column.field] ?? '–') : '' }}
                  </td>
                }
                <td class="text-left">
                  @switch (row.status) {
                    @case ('same') { = gleich }
                    @case ('changed') { ≠ abweichend }
                    @case ('manualOnly') { − nur manuell }
                    @case ('autoOnly') { + nur automatisch }
                  }
                </td>
              </tr>
            } @empty {
              <tr><td [attr.colspan]="2 + diffFields.length * 2" class="text-slate-500">Keine Aktionen.</td></tr>
            }
          </tbody>
        </table>
      </div>
      <div class="mt-1 text-[11px] text-slate-500">
        Zuordnung über den Feuerzeitpunkt der Cron; bei mehreren Aktionen zur selben Zeit in Reihenfolge.
      </div>
    </mat-expansion-panel>
  }

  <!-- ── Konfiguration des Standorts für die Übersetzung ───────────────────── -->
  @if (configGroups().length) {
    <mat-expansion-panel [expanded]="true">
      <mat-expansion-panel-header>
        <mat-panel-title>Konfiguration</mat-panel-title>
        <mat-panel-description>
          {{ locationState.currentLocation()?.name }}
          @if (response()?.configSolutionId != null) {
            · fehlende Werte aus Lösung #{{ response()?.configSolutionId }}
          }
        </mat-panel-description>
      </mat-expansion-panel-header>
      <div class="mb-2 flex items-center text-[13px]">
        <span class="text-slate-500">
          Werte, mit denen der Übersetzer rechnet: Standort-Metadaten, sonst globale Standardwerte
          (optimizer.schedule.*) bzw. Werte aus der Lösung.
        </span>
        <ng-container *ngTemplateOutlet="copyButton; context: { table: configTable, key: 'config' }" />
      </div>
      <div class="scroll-panel">
        <table class="debug-table config-table" #configTable>
          <thead>
            <tr>
              <th title="key">Schlüssel<span class="field-name">(key)</span></th>
              <th title="value">Wert<span class="field-name">(value)</span></th>
              <th class="text-left" title="unit">Einheit<span class="field-name">(unit)</span></th>
              <th class="text-left" title="source">Quelle<span class="field-name">(source)</span></th>
              <th class="text-left" title="from">Herkunft<span class="field-name">(from)</span></th>
            </tr>
          </thead>
          @for (group of configGroups(); track group.name) {
            <tbody>
              <tr class="config-group">
                <td colspan="5">{{ group.label }}</td>
              </tr>
              @for (value of group.values; track value.key) {
                <tr [class]="'config-' + value.source">
                  <td class="font-mono">{{ value.key }}</td>
                  <td class="font-mono">{{ value.value ?? '–' }}</td>
                  <td class="text-left">{{ value.unit ?? '' }}</td>
                  <td class="text-left">
                    <span class="config-badge">{{ configSourceLabels[value.source ?? ''] ?? value.source }}</span>
                  </td>
                  <td class="font-mono text-left text-slate-500">{{ value.from }}</td>
                </tr>
              }
            </tbody>
          }
        </table>
      </div>
    </mat-expansion-panel>
  }

  <!-- ── Diagramm der Optimierer-Ergebnisse ────────────────────────────────── -->
  @if (chartSolution(); as chartSol) {
    <mat-expansion-panel [expanded]="true">
      <mat-expansion-panel-header>
        <mat-panel-title>Diagramm Optimierer-Ergebnis</mat-panel-title>
        <mat-panel-description>Lösung #{{ chartSol.id }} · Version {{ chartSol.version ?? '–' }}</mat-panel-description>
      </mat-expansion-panel-header>
      @if (solutions().length > 1) {
        <mat-form-field appearance="outline" class="mb-2 min-w-[260px]" subscriptSizing="dynamic">
          <mat-label>Lösung</mat-label>
          <mat-select [value]="chartSol.id" (valueChange)="chartSolutionId.set($event)">
            @for (solution of solutions(); track solution.id) {
              <mat-option [value]="solution.id">#{{ solution.id }} · Version {{ solution.version ?? '–' }} · {{ solution.status }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      }
      <app-solution-chart [timesteps]="chartSol.timesteps ?? []" />
      <div class="mt-1 text-[11px] text-slate-500">
        Flüsse in kW (Ertrag und Last aus Wh je Zeitschritt umgerechnet, per Legende einblendbar), SoC in kWh, Preise in ct/kWh.
      </div>
    </mat-expansion-panel>
  }
  </mat-accordion>
</div>

<ng-template #copyButton let-table="table" let-key="key">
  <button
    type="button"
    class="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-900/15 px-1.5 py-0.5 text-[11px] font-normal text-slate-600 hover:bg-slate-900/5"
    (click)="copyTable(table, key)"
    title="Tabelle inkl. Überschriften als TSV in die Zwischenablage kopieren"
  >
    <mat-icon class="!h-[14px] !w-[14px] !text-[14px]">{{ copiedKey() === key ? 'check' : 'content_copy' }}</mat-icon>
    {{ copiedKey() === key ? 'Kopiert' : 'Kopieren' }}
  </button>
</ng-template>
